import * as React from "react";
import { MoreHorizontal, CheckCircle, Trash2, Droplet } from "lucide-react";
import { useChangeUserSatausMutation } from "@/store/studentApi";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { CustomAlertDialog } from "./AlertDialog";
import { RootState } from "@/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ActionDropdown = ({ student, studentName, studentLastName }) => {
  const [changeStatusFn, { isSuccess }] = useChangeUserSatausMutation();
  const { user } = useSelector((state: RootState) => state.user);

  const handleStatusChange = async (status: string) => {
    try {
      await changeStatusFn({
        Token: user?.token,
        data: {
          student,
          status: status,
        },
      });
      toast.success(`Status updated to ${status}!`);
    } catch (error) {
      console.log(error);
      toast.error(`Failed to update status to ${status}`);
    }
  };

  React.useEffect(() => {
    if (isSuccess) {
      toast.success("Status Updated! Check: By Filter");
    }
  }, [isSuccess]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <MoreHorizontal className="h-5 w-5 text-gray-500" />
        </button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-48 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-lg p-1 z-50">
        <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="p-0 focus:bg-transparent">
          <CustomAlertDialog
            triggerButton={
              <button className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-zinc-300 hover:bg-green-50 dark:hover:bg-green-950/30 transition-colors duration-150 rounded-sm text-left">
                <CheckCircle className="mr-3 h-4 w-4 text-gray-400 group-hover:text-green-500 transition-colors duration-150" />
                <span className="group-hover:text-green-700 dark:group-hover:text-green-400 font-medium">Activate</span>
              </button>
            }
            title="Activate Student"
            description={`Are you sure you want to activate ${studentName || "this student"} ${studentLastName || ""}?`}
            onConfirm={() => handleStatusChange("Active")}
          />
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="p-0 focus:bg-transparent">
          <CustomAlertDialog
            triggerButton={
              <button className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-zinc-300 hover:bg-orange-50 dark:hover:bg-orange-950/30 transition-colors duration-150 rounded-sm text-left">
                <Droplet className="mr-3 h-4 w-4 text-gray-400 group-hover:text-orange-500 transition-colors duration-150" />
                <span className="group-hover:text-orange-700 dark:group-hover:text-orange-400 font-medium">Drop</span>
              </button>
            }
            title="Drop Student"
            description={`Are you sure you want to drop ${studentName || "this student"} ${studentLastName || ""}?`}
            onConfirm={() => handleStatusChange("Disable")}
          />
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="p-0 focus:bg-transparent">
          <CustomAlertDialog
            triggerButton={
              <button className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-zinc-300 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors duration-150 rounded-sm text-left">
                <Trash2 className="mr-3 h-4 w-4 text-gray-400 group-hover:text-red-500 transition-colors duration-150" />
                <span className="group-hover:text-red-700 dark:group-hover:text-red-400 font-medium">Delete</span>
              </button>
            }
            title="Delete Student"
            description={`Are you sure you want to delete ${studentName || "this student"} ${studentLastName || ""}?`}
            onConfirm={() => handleStatusChange("Delete")}
          />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ActionDropdown;
